import React, { useMemo, useState } from 'react';
import { LightbulbIcon, MagnifyingGlassIcon, ClapperboardIcon, CogIcon, ChevronUpIcon } from '@/components/ui/Icons';
import { usePathname, useRouter } from 'next/navigation';
import { useModal } from '@/context/ModalContext';
import { useScroll } from '@/context/ScrollContext';
import { useRedirect } from '@/context/RedirectContext';
import { useSettings } from '@/context/SettingsContext';
import { MODAL_IDS } from '@/constants';
import { PRIMARY_CTA_TEXT, SITE_NAME, DOMAIN } from '@/config';

interface MobileTaskbarProps {
    scrollToTop: () => void;
    visibleSection: string;
}

const MobileTaskbar: React.FC<MobileTaskbarProps> = ({ scrollToTop, visibleSection }) => {
    const { openModal } = useModal();
    const { scrollToSection } = useScroll();
    const { redirect } = useRedirect();
    const { isFabParticlesEnabled } = useSettings();
    const pathname = usePathname();
    const router = useRouter();
    const [isTipsClicked, setIsTipsClicked] = useState(false);

    const handleRedirect = () => {
        redirect(SITE_NAME, `https://${DOMAIN}`);
    };

    const handleFabClick = () => {
        handleRedirect();
    };

    const handleTipsClick = () => {
        openModal(MODAL_IDS.TIPS);
        setIsTipsClicked(true);
    };

    const handleSearchClick = () => {
        if (pathname === '/') {
            scrollToSection('search', { behavior: 'smooth', block: 'center' });
        } else {
            // Use Next.js router instead of window.location.href to avoid full page reload
            router.push('/#search');
        }
    };

    const activeItem = useMemo(() => {
        switch (visibleSection) {
            case 'search':
                return 'search';
            case 'header':
            case 'features':
            case 'about':
            case 'faq':
                return 'home';
            default:
                return 'home';
        }
    }, [visibleSection]);
    

    return (
        <div className="mobile-taskbar-container md:hidden">
            <div className="mobile-taskbar">
                <button
                    onClick={handleTipsClick}
                    className="mobile-taskbar-item"
                    aria-label="Open Guide"
                >
                    <LightbulbIcon className={!isTipsClicked ? 'tips-icon-pulse' : ''} />
                </button>
                <button
                    onClick={handleSearchClick}
                    className={`mobile-taskbar-item ${activeItem === 'search' ? 'active' : ''}`}
                    aria-label="Search"
                >
                    <MagnifyingGlassIcon />
                </button>
                <a
                    href={`https://${DOMAIN}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.preventDefault();
                        handleFabClick();
                    }}
                    className="mobile-taskbar-fab"
                    aria-label={PRIMARY_CTA_TEXT}
                >
                    {/* Stardust particles */}
                    {isFabParticlesEnabled && [...Array(40)].map((_, i) => (
                        <span key={i} className={`stardust stardust-${i + 1}`} aria-hidden="true" />
                    ))}
                    <ClapperboardIcon className="pointer-events-none" />
                </a>
                <button
                    onClick={() => openModal(MODAL_IDS.SETTINGS)}
                    className="mobile-taskbar-item"
                    aria-label="Open Settings"
                >
                    <CogIcon className="settings-icon-spin" />
                </button>
                <button
                    onClick={scrollToTop}
                    className="mobile-taskbar-item"
                    aria-label="Scroll to top"
                >
                    <ChevronUpIcon />
                </button>
            </div>
        </div>
    );
};

export default MobileTaskbar;